import React, { useEffect, useState } from 'react';
import { construirReciboHtml } from '../utils/reciboHtml';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const apiFetch = async (path: string, token: string) => {
  const res = await fetch(`${BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Error ${res.status}`);
  return res.json();
};

// ─── Login ────────────────────────────────────────────────────────────────────

const LoginPortal: React.FC<{ onLogin: (token: string, cliente: any) => void }> = ({ onLogin }) => {
  const [form, setForm] = useState({ dni: '', telefono: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const ingresar = async () => {
    if (!form.dni || !form.telefono) { setError('Completa todos los campos'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${BASE}/auth/login-cliente`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { setError('DNI o teléfono incorrectos'); return; }
      const data = await res.json();
      onLogin(data.access_token, data.cliente);
    } catch { setError('Error de conexión'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#1e293b', borderRadius: 20, padding: 40, width: '100%', maxWidth: 400, border: '1px solid #334155' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>💧</div>
          <div style={{ color: '#38bdf8', fontWeight: 800, fontSize: 22 }}>Portal del Cliente</div>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Consulta tu cuenta de agua</div>
        </div>

        {error && (
          <div style={{ background: '#ef444415', border: '1px solid #ef444455', borderRadius: 10, padding: '10px 14px', marginBottom: 16, color: '#ef4444', fontSize: 13 }}>
            {error}
          </div>
        )}

        {[
          { label: 'Número de DNI', key: 'dni', type: 'text', placeholder: '12345678' },
          { label: 'Teléfono registrado', key: 'telefono', type: 'tel', placeholder: '987654321' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: 16 }}>
            <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>{f.label}</label>
            <input type={f.type} value={(form as any)[f.key]} placeholder={f.placeholder}
              onChange={e => setForm({ ...form, [f.key]: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && ingresar()}
              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: '12px 14px', color: 'white', fontSize: 15, boxSizing: 'border-box' as any, outline: 'none' }} />
          </div>
        ))}

        <button onClick={ingresar} disabled={loading}
          style={{ width: '100%', background: loading ? '#334155' : '#0ea5e9', border: 'none', color: 'white', borderRadius: 10, padding: '14px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 15, marginTop: 8 }}>
          {loading ? 'Ingresando...' : 'Ingresar a mi cuenta'}
        </button>

        <div style={{ color: '#475569', fontSize: 11, textAlign: 'center', marginTop: 20 }}>
          Si no puedes acceder, contacta a tu empresa distribuidora de agua.
        </div>
      </div>
    </div>
  );
};

// ─── Portal ───────────────────────────────────────────────────────────────────

const PortalCliente: React.FC = () => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('portal_token'));
  const [cliente, setCliente] = useState<any>(() => {
    try { return JSON.parse(localStorage.getItem('portal_cliente') || 'null'); } catch { return null; }
  });
  const [facturas, setFacturas] = useState<any[]>([]);
  const [pagos, setPagos] = useState<any[]>([]);
  const [plan, setPlan] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'inicio'|'facturas'|'pagos'>('inicio');
  const [reintentos, setReintentos] = useState(0);

  const handleLogin = (t: string, c: any) => {
    localStorage.setItem('portal_token', t);
    localStorage.setItem('portal_cliente', JSON.stringify(c));
    setToken(t); setCliente(c);
  };

  const cerrarSesion = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_cliente');
    setToken(null); setCliente(null);
  };

  useEffect(() => {
    if (!token || !cliente?.id) return;
    setLoading(true);
    setError('');
    Promise.all([
      apiFetch(`/facturas/cliente/${cliente.id}`, token),
      apiFetch(`/pagos/cliente/${cliente.id}`, token),
      apiFetch(`/planes-pago/cliente/${cliente.id}`, token).catch(() => []),
    ]).then(([f, p, planes]) => {
      setFacturas(Array.isArray(f) ? f : []);
      // Los pagos anulados no son cobros reales; no tiene sentido mostrarselos al cliente.
      setPagos(Array.isArray(p) ? p.filter((x: any) => x.estado !== 'anulado') : []);
      const activos = Array.isArray(planes) ? planes.filter((pl: any) => pl.estado === 'activo') : [];
      setPlan(activos[0] || null);
    }).catch(() => {
      // No dejar facturas/pagos en su valor anterior (podria mostrar "sin deuda" de forma
      // enganosa) ni asumir 0 -- se avisa al cliente y se ofrece reintentar.
      setError('No se pudo cargar tu información. Verifica tu conexión e intenta de nuevo.');
    }).finally(() => setLoading(false));
  }, [token, cliente?.id, reintentos]);

  if (!token || !cliente) return <LoginPortal onLogin={handleLogin} />;

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: '#1e293b', borderRadius: 20, padding: 40, width: '100%', maxWidth: 400, border: '1px solid #334155', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <div style={{ color: '#ef4444', fontSize: 14, marginBottom: 20 }}>{error}</div>
          <button
            onClick={() => setReintentos(r => r + 1)}
            style={{ background: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: 10, padding: '10px 24px', fontWeight: 700, cursor: 'pointer' }}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const verRecibo = (pago: any) => {
    const win = window.open('', '_blank', 'width=440,height=680');
    if (!win) return;
    win.document.write(construirReciboHtml({ ...pago, cliente }));
    win.document.close();
  };

  const pendientes = facturas.filter(f => f.estado === 'pendiente' || f.estado === 'vencido');
  const pagadas    = facturas.filter(f => f.estado === 'pagado');
  const deuda = pendientes.reduce((s, f) => s + Number(f.monto), 0);
  const estado = cliente.estado || 'activo';
  const estadoColor: Record<string, string> = { activo: '#22c55e', cortado: '#ef4444', pendiente_corte: '#f97316', pendiente_reconexion: '#f59e0b', reconectado: '#38bdf8' };
  const color = estadoColor[estado] || '#94a3b8';

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', fontFamily: 'system-ui,sans-serif' }}>
      {/* Header */}
      <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>💧</span>
          <div>
            <div style={{ color: '#38bdf8', fontWeight: 800, fontSize: 15 }}>Portal Cliente</div>
            <div style={{ color: '#64748b', fontSize: 11 }}>{cliente.nombres} {cliente.apellidos}</div>
          </div>
        </div>
        <button onClick={cerrarSesion}
          style={{ background: '#ef444422', border: '1px solid #ef444444', color: '#ef4444', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
          Cerrar sesión
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Cargando tu cuenta...</div>
      ) : (
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '20px 16px' }}>

          {/* Estado del servicio */}
          <div style={{ background: '#1e293b', borderRadius: 14, padding: '18px 20px', marginBottom: 16, border: `2px solid ${color}44` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div>
                <div style={{ color: '#f1f5f9', fontWeight: 800, fontSize: 17 }}>{cliente.nombres} {cliente.apellidos}</div>
                <div style={{ color: '#64748b', fontSize: 12 }}>DNI: {cliente.dni || '—'} · Código: {cliente.codigo || '—'}</div>
              </div>
              <span style={{ background: `${color}22`, color, border: `1px solid ${color}55`, borderRadius: 20, padding: '5px 14px', fontWeight: 700, fontSize: 12 }}>
                {estado === 'activo' ? 'ACTIVO' : estado.replace(/_/g, ' ').toUpperCase()}
              </span>
            </div>
            {cliente.paquete && (
              <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                <div style={{ color: '#94a3b8', fontSize: 12 }}>Plan: <span style={{ color: '#38bdf8', fontWeight: 600 }}>{cliente.paquete.nombre}</span></div>
                <div style={{ color: '#94a3b8', fontSize: 12 }}>Cuota: <span style={{ color: '#f1f5f9', fontWeight: 600 }}>S/. {Number(cliente.paquete.precio).toFixed(2)}/mes</span></div>
              </div>
            )}
          </div>

          {/* Resumen de deuda */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Deuda pendiente', value: `S/. ${deuda.toFixed(2)}`, color: deuda > 0 ? '#ef4444' : '#22c55e' },
              { label: 'Facturas pendientes', value: String(pendientes.length), color: pendientes.length > 0 ? '#f59e0b' : '#22c55e' },
              { label: 'Pagos realizados', value: String(pagadas.length), color: '#22c55e' },
            ].map(s => (
              <div key={s.label} style={{ background: '#1e293b', borderRadius: 12, padding: '14px', border: '1px solid #334155', textAlign: 'center' }}>
                <div style={{ color: '#64748b', fontSize: 11, marginBottom: 4 }}>{s.label}</div>
                <div style={{ color: s.color, fontWeight: 800, fontSize: 18 }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Plan de pago activo */}
          {plan && (
            <div style={{ background: '#1e293b', borderRadius: 14, padding: '16px 20px', marginBottom: 16, border: '1px solid #0ea5e944' }}>
              <div style={{ color: '#38bdf8', fontWeight: 700, marginBottom: 10 }}>💳 Plan de pago activo</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>{plan.cuotasPagadas} de {plan.numeroCuotas} cuotas pagadas</span>
                <span style={{ color: '#f1f5f9', fontWeight: 700 }}>S/. {Number(plan.montoCuota).toFixed(2)}/cuota</span>
              </div>
              <div style={{ background: '#0f172a', borderRadius: 4, height: 8, overflow: 'hidden', marginBottom: 4 }}>
                <div style={{ width: `${Math.round((plan.cuotasPagadas/plan.numeroCuotas)*100)}%`, background: '#0ea5e9', height: '100%' }} />
              </div>
              <div style={{ color: '#64748b', fontSize: 11, textAlign: 'right' }}>{Math.round((plan.cuotasPagadas/plan.numeroCuotas)*100)}%</div>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {([['inicio','Resumen'],['facturas',`Facturas (${pendientes.length})`],['pagos',`Historial (${pagadas.length})`]] as const).map(([t, l]) => (
              <button key={t} onClick={() => setTab(t)}
                style={{ flex: 1, background: tab === t ? '#0ea5e9' : '#1e293b', border: `1px solid ${tab === t ? '#0ea5e9' : '#334155'}`, color: tab === t ? 'white' : '#94a3b8', borderRadius: 8, padding: '10px', cursor: 'pointer', fontWeight: tab === t ? 700 : 400, fontSize: 13 }}>
                {l}
              </button>
            ))}
          </div>

          {/* Tab: Resumen */}
          {tab === 'inicio' && (
            <div>
              {estado !== 'activo' && (
                <div style={{ background: '#ef444415', border: '1px solid #ef444455', borderRadius: 12, padding: '14px 18px', marginBottom: 12 }}>
                  <div style={{ color: '#ef4444', fontWeight: 700 }}>⚠️ Servicio con restricción</div>
                  <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>
                    {estado === 'cortado' ? 'Tu servicio fue suspendido por deuda. Paga tus facturas pendientes y contacta al administrador para reconectarte.'
                      : estado === 'pendiente_corte' ? 'Tienes un corte programado. Regulariza tu deuda lo antes posible.'
                      : 'Contáctate con la empresa de agua para más información.'}
                  </div>
                </div>
              )}
              {pendientes.length === 0 ? (
                <div style={{ background: '#22c55e15', border: '1px solid #22c55e33', borderRadius: 12, padding: '18px 20px', textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                  <div style={{ color: '#22c55e', fontWeight: 700 }}>Estás al día con tus pagos</div>
                  <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>No tienes facturas pendientes</div>
                </div>
              ) : (
                <div style={{ background: '#f59e0b15', border: '1px solid #f59e0b33', borderRadius: 12, padding: '14px 18px' }}>
                  <div style={{ color: '#f59e0b', fontWeight: 700, marginBottom: 8 }}>⏰ Tienes {pendientes.length} factura(s) pendiente(s)</div>
                  {pendientes.slice(0, 3).map(f => (
                    <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f59e0b22' }}>
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>
                        {MESES[(f.mes||1)-1]} {f.anio} · Vence {f.fechaVencimiento?.split('T')[0] || ''}
                      </span>
                      <span style={{ color: '#f59e0b', fontWeight: 700 }}>S/. {Number(f.monto).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab: Facturas */}
          {tab === 'facturas' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {facturas.length === 0 ? (
                <div style={{ color: '#64748b', textAlign: 'center', padding: 32 }}>Sin facturas registradas</div>
              ) : facturas.map(f => (
                <div key={f.id} style={{ background: '#1e293b', borderRadius: 12, padding: '14px 16px', border: `1px solid ${f.estado === 'pagado' ? '#22c55e33' : f.estado === 'vencido' ? '#ef444433' : '#f59e0b33'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ color: '#38bdf8', fontWeight: 700, fontSize: 13 }}>{f.numero}</div>
                      <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>
                        {MESES[(f.mes||1)-1]} {f.anio} · Vence: {f.fechaVencimiento?.split('T')[0] || '—'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 800, fontSize: 16, color: f.estado === 'pagado' ? '#22c55e' : f.estado === 'vencido' ? '#ef4444' : '#f59e0b' }}>
                        S/. {Number(f.monto).toFixed(2)}
                      </div>
                      <div style={{ fontSize: 10, color: f.estado === 'pagado' ? '#22c55e' : f.estado === 'vencido' ? '#ef4444' : '#f59e0b', fontWeight: 700, marginTop: 2 }}>
                        {f.estado === 'pagado' ? 'PAGADO' : f.estado === 'vencido' ? 'VENCIDO' : 'PENDIENTE'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tab: Historial de pagos */}
          {tab === 'pagos' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pagos.length === 0 ? (
                <div style={{ color: '#64748b', textAlign: 'center', padding: 32 }}>Sin pagos registrados</div>
              ) : pagos.map(p => {
                const fecha = new Date(p.createdAt);
                return (
                  <div key={p.id} style={{ background: '#1e293b', borderRadius: 12, padding: '14px 16px', border: '1px solid #22c55e33', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ color: '#22c55e', fontWeight: 700, fontSize: 15 }}>S/. {Number(p.monto).toFixed(2)}</div>
                      <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>
                        {fecha.toLocaleDateString('es-PE')} · {p.metodo || 'efectivo'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: '#38bdf8', fontSize: 11, background: '#38bdf822', padding: '3px 10px', borderRadius: 20 }}>Pagado</span>
                      <button
                        onClick={() => verRecibo(p)}
                        title="Ver recibo"
                        style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', fontSize: 13 }}>
                        🖨
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ color: '#475569', fontSize: 11, textAlign: 'center', marginTop: 32 }}>
            Para consultas contacta a tu empresa distribuidora de agua.
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalCliente;
